import * as R from 'ramda';
import { CustomError, TechnicalError } from '../../../common/errors';
import Result from '../../../common/result';

export type BackgroundTaskResult = Result<CustomError>;

export interface BackgroundTask {
  /** Perform a promise based background task that returns no value or an error result */
  runBackgroundTask(): Promise<BackgroundTaskResult>;
}

export default class ValidationBackgroundProcessing {
  private backgroundTasks: Promise<BackgroundTaskResult>[] = [];

  /** Enqueue a task in the background. Ensure the previous tasks completes before executing the requested task */
  private static async enqueueInBackground(
    previousTask: Promise<BackgroundTaskResult> | null,
    task: BackgroundTask,
  ): Promise<BackgroundTaskResult> {
    const previousTaskResult = await previousTask;
    if (previousTaskResult?.isFailed()) {
      return Result.failed(
        new TechnicalError('Background task aborted, previous task failed', previousTaskResult.failure()),
      );
    }

    return task.runBackgroundTask();
  }

  /** Add a task for background processing */
  public addTask(task: BackgroundTask): void {
    const previousTask = R.last(this.backgroundTasks);
    this.backgroundTasks.push(ValidationBackgroundProcessing.enqueueInBackground(previousTask || null, task));
  }

  /**
   * Wait for all background processing tasks to complete
   * @returns Result.failed if any of the tasks failed, otherwise result.success with no value
   */
  public async waitForTasks(): Promise<BackgroundTaskResult> {
    for (let i = 0; i < this.backgroundTasks.length; i += 1) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const taskResult = await this.backgroundTasks[i];
        if (taskResult.isFailed()) {
          return Result.failed(taskResult.failure());
        }
      } catch (error) {
        return Result.failed(new TechnicalError('Unexpected error thrown by background task', error as Error));
      }
    }

    return Result.noValueSuccess();
  }
}
